import { View, SxProp, useDripsyTheme, styled, Image } from 'dripsy';
import { useNavigation } from '@react-navigation/native';
import React, { useContext, useEffect, useState } from 'react';
import { useAsyncStorage } from '@/devtools/hooks/useAsyncStorage';
import { formatAmountByDecimals } from '@/utils/numberFormatting';
import { formatAmountByDecimal } from '@/features/account-management/utils';
import { PageItem } from '@/features/earn/components/page/PageItem';
import { H6, TextBody, TextCaption, TextSmall, TextTitle } from '@/design-system/components/typography';
import { CurrencySelector } from '@/design-system/components/currencySelector/CurrencySelector';
import { UniIconButton } from '@/design-system/components/buttons';
import { Row } from '@/design-system/components/layout';
import { UniCheckbox, UniInput } from '@/design-system/components/inputs';
import { Icon } from '@/design-system/components/icons/Icon';
import { CurrenciesValues } from '@/features/account-management/options';
import { LinearGradient } from 'expo-linear-gradient';
import { RootStackRoutes } from 'types';
import { AccountManagementStackRoutes } from '@/features/account-management/account.navigator';
import { PageContainerNative } from '@/features/account-management/screens/page/PageContainerNative';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@/design-system/components/tooltip/Tooltip';
import { useAccountBalance } from '@/features/account-management/screens/details/hooks/useAccount.hook';
import { useCard } from '@/features/account-management/screens/details/hooks/useCard.hook';
import { LayoutContext } from 'contexts/LayoutContext';
import { UiAccountAssetBalance } from '@utx/utx-access-client';
import { UnionType } from '@/features/bill-payment/enum';
import { UtxHasFeature } from '@/design-system/components/hasFeature/utxHasFeature';

export interface AccountDetailsProps {
  account: string;
  tab?: number;
  record: any;
}

export const AccountDetails: React.FC<AccountDetailsProps> = (props) => {
  const { account, tab, record } = props;
  const navigation = useNavigation();
  const { theme } = useDripsyTheme();
  const layout = useContext(LayoutContext);
  const { t } = useTranslation('account-management');
  const { value: show, set: setShow } = useAsyncStorage<boolean>('showAmount');
  const [showCard, setShowCard] = useState<boolean>(true);
  const showChange = (value: boolean) => {
    setShow(value);
  };
  const Gradient = styled(LinearGradient, {})({});
  const showChangeCard = (value: boolean) => {
    setShowCard(value);
  };
  const [currency, setCurrency] = useState('HKD');

  const isPositive = (value: string) => {
    return value.startsWith('+');
  };
  const isNegative = (value: string) => {
    return (value && value.toString().startsWith('-')) || false;
  };
  const hideShowBalance = (balance: string) => {
    return show ? '******' : balance;
  };

  const [search, setSearch] = useState<string | undefined>(undefined);
  const { balances, isLoadingAccountBalances, reloadAccountBalances, accountsTransfers } = useAccountBalance({
    accountId: account,
    asset: search
  });
  const [accountBalances, setAccountBalances] = useState<UiAccountAssetBalance[]>([]);
  useEffect(() => {
    setAccountBalances(
      (balances && balances[0] && balances[0].assets?.sort((a, b) => (b.balanceUsdEquivalent || 0) - (a.balanceUsdEquivalent || 0))) || []
    );
  }, [balances]);
  const [assetsData, setAssetsData] = useState<UiAccountAssetBalance[]>([]);

  const [accountsOverview, setAccountsOverview] = useState<{
    creditBalanceInHkd: number;
    creditBalanceInUsd: number;
    assetBalanceInHkd: number;
    assetBalanceInUsd: number;
  }>({ creditBalanceInHkd: 0, creditBalanceInUsd: 0, assetBalanceInHkd: 0, assetBalanceInUsd: 0 });
  useEffect(() => {
    if (!isLoadingAccountBalances) {
      let assetHkdEquivalentAmount = 0;
      let assetUsdEquivalentAmount = 0;
      if (accountBalances && accountBalances.length) {
        accountBalances.forEach((accountBalance: any) => {
          assetHkdEquivalentAmount += accountBalance?.balanceHkdEquivalent;
          assetUsdEquivalentAmount += accountBalance?.balanceUsdEquivalent;
        });
        setAccountsOverview({
          ...accountsOverview,
          assetBalanceInHkd: assetHkdEquivalentAmount,
          assetBalanceInUsd: assetUsdEquivalentAmount
        });
      } else {
        setAccountsOverview({
          ...accountsOverview,
          assetBalanceInHkd: assetHkdEquivalentAmount,
          assetBalanceInUsd: assetUsdEquivalentAmount
        });
      }

      setAssetsData(accountBalances);
    }
  }, [isLoadingAccountBalances, accountBalances]);

  const [hideLowBalances, setHideLowBalances] = useState<boolean>(false);
  const changeHideLowBalances = (value: boolean) => {
    setHideLowBalances(value);
    const assets = accountBalances.filter(
      (asset: any) =>
        !value || (value && (!asset['pctChange24hInUsd'] || (asset['pctChange24hInUsd'] && !asset['pctChange24hInUsd'].startsWith('-'))))
    );
    setAssetsData(assets);
  };

  const transactionHistory = () => {
    navigation.navigate(RootStackRoutes.AccountManagement, {
      screen: AccountManagementStackRoutes.AccountTransactionHistory,
      params: { account }
    });
  };

  const { cardAccounts, isLoadingCardAccounts } = record?.cardAccountId
    ? useCard({ cardAccountId: record?.cardAccountId })
    : { cardAccounts: [], isLoadingCardAccounts: true };
  useEffect(() => {
    if (!isLoadingCardAccounts) {
      let balanceHkdEquivalentAmount = 0;
      let balanceUsdEquivalentAmount = 0;
      if (cardAccounts && cardAccounts.length) {
        cardAccounts.forEach((cardAccount: any) => {
          balanceHkdEquivalentAmount += cardAccount?.balanceHkdEquivalent || 0;
          balanceUsdEquivalentAmount += cardAccount?.balanceUsdEquivalent || 0;
        });
        setAccountsOverview({
          ...accountsOverview,
          creditBalanceInHkd: balanceHkdEquivalentAmount,
          creditBalanceInUsd: balanceUsdEquivalentAmount
        });
      } else {
        setAccountsOverview({
          ...accountsOverview,
          creditBalanceInHkd: balanceHkdEquivalentAmount,
          creditBalanceInUsd: balanceUsdEquivalentAmount
        });
      }
    }
  }, [isLoadingCardAccounts, cardAccounts]);

  const [netAssetValues, setNetAssetValues] = useState<{ navInHkd: number; navInUsd: number }>({
    navInHkd: 0,
    navInUsd: 0
  });
  useEffect(() => {
    setNetAssetValues({
      navInHkd: accountsOverview.assetBalanceInHkd - accountsOverview.creditBalanceInHkd,
      navInUsd: accountsOverview.assetBalanceInUsd - accountsOverview.creditBalanceInUsd
    });
  }, [
    accountsOverview.assetBalanceInHkd,
    accountsOverview.assetBalanceInUsd,
    accountsOverview.creditBalanceInHkd,
    accountsOverview.creditBalanceInUsd
  ]);

  const flatlistHight = layout.dimensions.height - 402;

  const funcAccountsTransfers = (data: any, onSuccess?: (data: any) => void, onError?: (error: any) => void) => {
    accountsTransfers(data, onSuccess, onError);
  };

  return (
    <View sx={styles.contentContainer}>
      <PageContainerNative>
        <Row sx={{ justifyContent: 'space-between', alignItems: 'center', px: '$4', mt: 10, mb: 10 }}>
          <UniIconButton
            icon="arrowLeft"
            options={{ iconStyles: { color: theme?.colors?.$white as string } }}
            onPress={() => navigation.getParent()?.goBack()}></UniIconButton>
          <View sx={{ textAlign: 'center' }}>
            <TextTitle bold sx={{ color: theme?.colors?.$white as string }}>
              {record?.name}
            </TextTitle>
            <TextSmall sx={{ mt: '$1', color: theme?.colors?.$white as string }}>{record?.accountNumber}</TextSmall>
          </View>
          <UniIconButton
            icon="closeCircle"
            options={{ iconStyles: { color: theme?.colors?.$white as string, width: 24, height: 24 } }}
            onPress={() => navigation.getParent()?.goBack()}></UniIconButton>
        </Row>
      </PageContainerNative>
      <View sx={styles.overviewCard}>
        <View sx={styles.overviewHeader}>
          <View sx={styles.overviewHeaderLeft}>
            <TextTitle bold>{t('card-account-management::account-page.title--overview')}</TextTitle>
            <View sx={{ flexDirection: 'row', alignItems: 'center', gap: '$4' }}>
              <CurrencySelector options={CurrenciesValues} onValueChange={(value) => setCurrency(value)} />
              <UniIconButton
                icon="navHistory"
                options={{ iconStyles: { width: 24, height: 24 } }}
                onPress={transactionHistory}></UniIconButton>
            </View>
          </View>
        </View>
        <View sx={styles.overviewContent}>
          <View>
            <Row sx={{ alignItems: 'center', gap: '$1' }}>
              <TextBody semiBold secondary>
                {t('card-account-management::account-page.text--net-asset-value', { currency })}
              </TextBody>
              <UniIconButton icon={show ? 'hide' : 'show'} onPress={(e) => showChange(!show)} />
            </Row>
            <Row sx={{ mt: '$1', justifyContent: 'space-between' }}>
              <H6 bold sx={{ color: theme?.colors?.$secondary as string }}>
                {hideShowBalance(
                  formatAmountByDecimal({
                    value: currency == 'HKD' ? netAssetValues.navInHkd : netAssetValues.navInUsd,
                    suffix: currency,
                    decimals: 2
                  })
                )}
              </H6>
            </Row>
          </View>
          {showCard && (
            <View
              sx={{
                alignItems: 'center',
                gap: '$2',
                flex: 1,
                mt: '$5'
              }}>
              <Row sx={{ width: '100%', alignItems: 'center', gap: '$1', justifyContent: 'space-between' }}>
                <TextBody semiBold secondary>
                  {t('card-account-management::account-page.text--asset')}
                </TextBody>
                <TextBody semiBold secondary>
                  {t('card-account-management::account-page.text--card-account-balance')}
                </TextBody>
              </Row>
              <Row sx={{ width: '100%', alignItems: 'center', gap: '$1', justifyContent: 'space-between' }}>
                <TextBody semiBold>
                  {hideShowBalance(
                    formatAmountByDecimal({
                      value: currency == 'HKD' ? accountsOverview.assetBalanceInHkd : accountsOverview.assetBalanceInUsd,
                      suffix: currency,
                      decimals: 2
                    })
                  )}
                </TextBody>
                <TextBody semiBold>
                  {hideShowBalance(
                    formatAmountByDecimal({
                      value:
                        currency == 'HKD'
                          ? accountsOverview.creditBalanceInHkd
                              .toString()
                              .substring(isNegative(accountsOverview.creditBalanceInHkd.toString()) ? 1 : 0)
                          : accountsOverview.creditBalanceInUsd
                              .toString()
                              .substring(isNegative(accountsOverview.creditBalanceInUsd.toString()) ? 1 : 0),
                      suffix: `${currency} ${isNegative(accountsOverview.creditBalanceInHkd.toString()) ? '[CR]' : ''}`,
                      decimals: 2
                    })
                  )}
                </TextBody>
              </Row>
            </View>
          )}
          <View sx={styles.overviewOperations}>
            <UtxHasFeature feature={'ACCESS_TRANSFER'} variation={'enabled'}>
              <View sx={styles.accountActionBtn}>
                <TextBody
                  sx={styles.accountButton}
                  onPress={() => {
                    navigation.navigate(RootStackRoutes.AccountManagement, {
                      screen: AccountManagementStackRoutes.AccountsTransfers,
                      params: {
                        account: account,
                        tab: tab,
                        record: record,
                        func: funcAccountsTransfers,
                        refresh: () => {
                          reloadAccountBalances();
                        }
                      }
                    });
                  }}>
                  <Icon name="transferUTGLWeb" />
                </TextBody>
                <TextSmall semiBold>{t('account-transfer::glossary.button-transfer')}</TextSmall>
              </View>
            </UtxHasFeature>
            <UtxHasFeature feature={'ACCESS_DIGITAL_DEPOSIT'} variation={'enabled'}>
              <View sx={styles.accountActionBtn}>
                <TextBody
                  sx={styles.accountButton}
                  onPress={() => {
                    navigation.navigate(RootStackRoutes.AccountManagement, {
                      screen: AccountManagementStackRoutes.DigitalDeposit,
                      params: {
                        account: account,
                        record:record,
                        refresh: () => {}
                      }
                    });
                  }}>
                  <Icon name="injectUTGLWeb" />
                </TextBody>
                <TextSmall semiBold>{t('digital-injection::glossary.digital-injection')}</TextSmall>
              </View>
            </UtxHasFeature>
            <UtxHasFeature feature={'UNIONPAY_REMITTANCE'} variation={'beta'}>
              <View sx={styles.accountActionBtn}>
                <TextBody
                  sx={styles.accountButton}
                  onPress={() => {
                    navigation.navigate(RootStackRoutes.AccountManagement, {
                      screen: AccountManagementStackRoutes.UnionPay,
                      params: {
                        unionType: UnionType.ACCOUNT,
                        refresh: () => {
                          reloadAccountBalances();
                        }
                      }
                    });
                  }}>
                  <Icon name="unionPayBP" width={30} height={30} />
                </TextBody>
                <TextSmall semiBold>{t('unionPay::payment-instruction.glossary.unionpay')}</TextSmall>
              </View>
            </UtxHasFeature>
          </View>
        </View>
      </View>
      <View sx={styles.assetCard}>
        <PageItem sx={styles.card}>
          <View sx={styles.assetHeader}>
            <TextTitle bold>{t('card-account-management::account-page.text--assets')}</TextTitle>
            <UniCheckbox
              label={t('card-account-management::account-page.text--hide-low-balances')}
              value={hideLowBalances}
              verticalAlignCenter
              onValueChange={(value) => changeHideLowBalances(value)}
            />
          </View>
          <View sx={styles.assetOperation}>
            <UniInput
              wrapperSx={{ flexDirection: 'row', justifyContent: 'flex-end' }}
              sx={{ width: '100%' }}
              size="default"
              prefixIcon="search"
              placeholder={t('card-account-management::account-page.text--search-by-asset')}
              onValueChange={(value) => setSearch(value)}
            />
          </View>
          <View sx={{ ...styles.assetContent, height: flatlistHight }}>
            {assetsData && assetsData.length ? (
              assetsData.map((item, i) => (
                <View>
                  <Row key={i} sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', pt: '$2', pb: '$2' }}>
                    <View sx={{ flexDirection: 'row', gap: '$3', alignItems: 'center' }}>
                      {item?.iconUrl ? (
                        <Image sx={{ width: 28, height: 28 }} source={{ uri: item?.iconUrl }} />
                      ) : (
                        <Icon name={item.asset?.toLowerCase()} width={28} height={28} />
                      )}
                      <View>
                        <TextBody semiBold>{item.asset}</TextBody>
                        <Row sx={{ alignItems: 'center', gap: '$2' }}>
                          <Tooltip
                            content={
                              <TextBody sx={styles.tooltipText}>
                                {formatAmountByDecimals({
                                  value: Number(item.usdRate).toString(),
                                  prefix: '1 USD ≈',
                                  suffix: item.asset
                                })}
                              </TextBody>
                            }
                            position="left"
                            contentTextMinWidth={120}>
                            <TextCaption secondary>
                              $
                              {formatAmountByDecimals({
                                value: Number(item.usdRate).toString()
                              })}
                            </TextCaption>
                          </Tooltip>
                          <Row sx={{ alignItems: 'center' }}>
                            <TextCaption
                              success={!isNegative(item.pctChange24hInUsd || '')}
                              danger={isNegative(item.pctChange24hInUsd || '')}>
                              (
                            </TextCaption>
                            <Icon
                              name={isNegative(item.pctChange24hInUsd || '') ? 'arrowDownPoint' : 'arrowUpPoint'}
                              color={
                                isNegative(item.pctChange24hInUsd || '')
                                  ? (theme?.colors?.$danger as string)
                                  : (theme?.colors?.$success as string)
                              }
                            />
                            <TextCaption
                              success={!isNegative(item.pctChange24hInUsd || '')}
                              danger={isNegative(item.pctChange24hInUsd || '')}>
                              {formatAmountByDecimal({ value: item.pctChange24hInUsd || 0, decimals: 2 })}
                            </TextCaption>
                            <TextCaption
                              success={!isNegative(item.pctChange24hInUsd || '')}
                              danger={isNegative(item.pctChange24hInUsd || '')}>
                              %)
                            </TextCaption>
                          </Row>
                        </Row>
                      </View>
                    </View>
                    <View sx={{ alignItems: 'flex-end' }}>
                      <TextBody semiBold sx={{ color: theme?.colors?.$secondary as string }}>
                        {formatAmountByDecimals({ value: item.balance || 0, suffix: item.asset })}
                      </TextBody>
                      <TextCaption secondary>
                        ≈ {formatAmountByDecimal({ value: item.balanceUsdEquivalent || 0, suffix: 'USD', decimals: 2 })}
                      </TextCaption>
                    </View>
                  </Row>
                </View>
              ))
            ) : (
              <View></View>
            )}
          </View>
        </PageItem>
      </View>
    </View>
  );
};

enum StyleName {
  contentContainer = 'contentContainer',
  card = 'card',
  overviewCard = 'overviewCard',
  assetCard = 'assetCard',
  transactionCard = 'transactionCard',
  overviewHeader = 'overviewHeader',
  overviewContent = 'overviewContent',
  overviewOperations = 'overviewOperations',
  assetHeader = 'assetHeader',
  assetOperation = 'assetOperation',
  assetContent = 'assetContent',
  transactionHeader = 'transactionHeader',
  transactionContent = 'transactionContent',
  transactionFooter = 'transactionFooter',
  accountActionBtn = 'accountActionBtn',
  accountButton = 'accountButton',
  overviewHeaderLeft = 'overviewHeaderLeft',
  showInfo = 'showInfo',
  infoContainer = 'infoContainer',
  tooltipText = 'tooltipText'
}

const styles: Record<StyleName, SxProp> = {
  contentContainer: {
    // display: 'grid',
    gridTemplateColumns: '1fr',
    gridTemplateAreas: `
      "overview assets"
      "transaction assets"
      "transaction assets"
      `,
    gridTemplateRows: 'repeat(3, auto)'
  },
  card: {
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: '$4',
    borderRadius: 0
    // marginBottom: 50
  },
  overviewCard: {
    gridArea: 'overview',
    padding: '$4',
    paddingBottom: 8,
    backgroundColor: '$white',
    borderTopLeftRadius: 15,
    borderTopRightRadius: 15
  },
  overviewHeader: { flexDirection: 'row', justifyContent: 'space-between' },
  overviewContent: { mt: '$6', flexDirection: 'column' },
  overviewOperations: { flexDirection: 'row', gap: '$3', height: '$15', mt: '$5', mb: '$5', justifyContent: 'space-around' },
  assetCard: { gridArea: 'assets', marginTop: '$1' },
  assetHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  assetOperation: { mt: '$6', flexDirection: 'column', gap: '$3' },
  assetContent: { mt: '$2', flexDirection: 'column', gap: '$3' },
  transactionCard: { gridArea: 'transaction', mt: '$5' },
  transactionHeader: { mt: '$7', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', zIndex: 1 },
  transactionContent: { mt: '$5' },
  transactionFooter: { mt: '$5', flexDirection: 'row', justifyContent: 'space-between' },
  accountActionBtn: {
    // width: 100,
    alignItems: 'center',
    gap: '$1'
  },
  accountButton: {
    width: 40,
    height: 40,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 100,
    backgroundColor: '$filterSelected'
  },
  overviewHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: '$5',
    width: '100%',
    justifyContent: 'space-between'
  },
  showInfo: {
    flexDirection: 'row'
  },
  infoContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  tooltipText: {
    color: '$textTooltip',
    whiteSpace: 'nowrap',
    textAlign: 'center'
  }
};
